// Reference-counted body scroll lock, shared across every component that needs to freeze
// background scrolling while an overlay is open (Modal, ChoosePlan, BrandDnaModal, …).
//
// Why ref-counting: the lock mutates the single global <body>. When two overlays are open at
// once, a naive "capture the live body style on open, restore it on close" approach makes the
// second overlay capture the already-locked state as its "original" — and whichever closes first
// restores a clean body while the other later restores the locked state, leaving `position: fixed`
// stuck on <body> and freezing the whole page until reload. (Real case: Photoshoot flat flow,
// CategorySelectModal → AddProduct.)
//
// With a shared counter the FIRST lock snapshots the original styles + scroll position and applies
// the lock; nested locks only bump the counter; the LAST unlock restores the snapshot — regardless
// of open/close order. O(1); <body> is written only on the first-lock / last-unlock transitions.
//
// The position:fixed technique (vs. plain overflow:hidden) also preserves the scroll position so
// the page doesn't jump to the top when an overlay opens/closes.

let scrollLockCount = 0;
let savedScrollY = 0;
let savedBodyStyles: { position: string; top: string; width: string; overflow: string } | null = null;

export const lockBodyScroll = () => {
  scrollLockCount += 1;
  if (scrollLockCount > 1) return; // already locked by an earlier overlay

  const body = document.body;
  savedScrollY = window.scrollY;
  savedBodyStyles = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };

  body.style.position = "fixed";
  body.style.top = `-${savedScrollY}px`;
  body.style.width = "100%";
  body.style.overflow = "hidden";
};

export const unlockBodyScroll = () => {
  if (scrollLockCount === 0) return; // guard against an unbalanced unlock
  scrollLockCount -= 1;
  if (scrollLockCount > 0) return; // another overlay is still open — keep the lock

  const body = document.body;

  if (savedBodyStyles) {
    body.style.position = savedBodyStyles.position;
    body.style.top = savedBodyStyles.top;
    body.style.width = savedBodyStyles.width;
    body.style.overflow = savedBodyStyles.overflow;
    savedBodyStyles = null;
  }

  window.scrollTo(0, savedScrollY);
};
